import { useRef } from 'react'

/**
 * Button that copies value to the clipboard (or value appended to the current origin when
 * isUrl is set), briefly toggling a "done" state on the button to confirm the copy.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.icon="fa-regular fa-copy"] - Icon class name to display on the button.
 * @param {string} props.value - Value to copy, or path to copy as a full URL when isUrl is true.
 * @param {boolean} [props.isUrl] - When true, copies the current window location origin + value.
 * @param {string} [props.className] - Extra class name to add to the button.
 * @returns {JSX.Element}
 */
const CopyButton = ({ icon = 'fa-regular fa-copy', value, isUrl, className }) => {
  const button = useRef()
  const componentClassnames = ['copy-button']

  className && componentClassnames.push(className)

  /**
   * Copies `value` (or the current origin + `value` when `isUrl` is set) to the clipboard,
   * then toggles a "done" class on the button for one second to confirm the copy.
   *
   * @param {Event} e - Click event from the button.
   * @returns {void}
   */
  const copy = e => {
    let copyTimeout
    const text = isUrl ? `${window.location.origin}${value}` : value

    console.info('copied: %s', text)

    e.preventDefault()
    copyTimeout && clearTimeout(copyTimeout)

    navigator.clipboard
      .writeText(text)
      .then(() => {
        button.current.classList.add('done')
      })
      .finally(() => {
        copyTimeout = setTimeout(() => {
          button.current.classList.remove('done')
        }, 1000)
      })
  }

  return (
    <button
      ref={button}
      className={componentClassnames.join(' ')}
      onClick={copy}
    >
      <i className={icon} />
      <i className="fa-solid fa-check" />
    </button>
  )
}

export default CopyButton
